/**
 * Canvas Context (V2)
 *
 * Provides APIs for programmatic canvas manipulation.
 * Components inside the canvas tree use useCanvas() to add/remove/update
 * nodes and edges without directly importing React Flow hooks.
 *
 * Key V2 changes:
 * - addEdge enforces areTypesCompatible() (BUILD_ORDER Q1)
 * - Uses crypto.randomUUID() via createNode (BUILD_ORDER Q2)
 * - Separate from NodeHoverContext (separate file)
 */

import { createContext, useContext, useCallback, useMemo } from 'react'
import { useReactFlow } from 'reactflow'
import {
  assertNoDirectAssetInputPortWrites,
  omitDirectAssetInputPortWrites,
} from './assetPortGuards'
import { areTypesCompatible } from './portTypes'
import { nodeSchemas, createNode, type NodeDataRecord } from './nodeRegistry'
import type {
  ReactFlowCanvasNode,
  ReactFlowCanvasEdgeData,
  ReactFlowCanvasEdge,
  ReactFlowNodeData,
} from '../../hooks/useYCanvas'

/**
 * Position on the canvas
 */
export interface CanvasPosition {
  x: number
  y: number
}

/**
 * Canvas API interface - all 10 methods per spec
 */
export interface CanvasContextValue {
  // DAG manipulation
  addNode: (
    type: string,
    position: CanvasPosition,
    data?: NodeDataRecord,
    options?: { autoExecute?: boolean; parentId?: string }
  ) => string
  addEdge: (
    source: string,
    sourceHandle: string,
    target: string,
    targetHandle: string
  ) => void
  removeNode: (nodeId: string) => void
  removeNodes?: (nodeIds: string[]) => void
  removeEdge: (edgeId: string) => void
  duplicateNode: (nodeId: string) => string | undefined

  // Node data
  updateNodeData: (nodeId: string, updates: NodeDataRecord) => void
  persistNodeData: (nodeId: string, updates: NodeDataRecord) => void
  updateNodeParent: (nodeId: string, parentId: string | null) => void
  updateNodePosition: (nodeId: string, x: number, y: number) => void
  transact?: (fn: () => void) => void

  // Execution
  executeNode: (nodeId: string) => void

  // Node preparation (create without adding to ReactFlow state)
  prepareNode?: (
    type: string,
    position: CanvasPosition,
    data?: NodeDataRecord,
  ) => { id: string; type: string; position: CanvasPosition; data: NodeDataRecord }
  syncPreparedNodeToYDoc?: (node: { id: string; type: string; position: CanvasPosition; data: NodeDataRecord }, parentId?: string) => void

  // State access
  getNode: (nodeId: string) => ReactFlowCanvasNode | undefined
  getNodes: () => ReactFlowCanvasNode[]

  // Edge sync for reference selectors (replaces RFC-0523 direct-write hack)
  replacePortEdges: (
    targetId: string,
    targetHandle: string,
    newEdges: Array<{ source: string; sourceHandle: string; toIndex?: number }>
  ) => void
}

/**
 * Canvas context - null outside of a provider
 */
export const CanvasContext = createContext<CanvasContextValue | null>(null)

/**
 * Hook to access canvas APIs.
 * @throws Error if used outside of CanvasProvider
 */
export function useCanvas(): CanvasContextValue {
  const ctx = useContext(CanvasContext)
  if (!ctx) {
    throw new Error('useCanvas must be used within a CanvasProvider')
  }
  return ctx
}

/**
 * Optional hook that returns null instead of throwing when outside provider.
 */
export function useCanvasOptional(): CanvasContextValue | null {
  return useContext(CanvasContext)
}

// =============================================================================
// Hook to build the CanvasContextValue from React Flow internals
// =============================================================================

/**
 * Resolves the port type for a given node and handle.
 * Returns undefined if the schema or port is not found.
 */
function resolvePortType(
  nodeType: string | undefined,
  handleId: string,
  direction: 'inputs' | 'outputs'
): string | undefined {
  if (!nodeType) return undefined
  const schema = nodeSchemas[nodeType]
  if (!schema) return undefined
  const ports = schema[direction]
  const port = ports.find((p) => p.id === handleId)
  return port?.type
}

/**
 * Hook that builds a CanvasContextValue from React Flow state.
 * Used by the workspace page to provide context to all canvas nodes.
 *
 * @param onUpdateNodeData - callback for user-initiated config changes (Y doc + debounced backend sync)
 * @param onPersistNodeData - callback for execution results (Y doc only, no backend sync)
 * @param onExecuteNode - callback to queue a node for execution
 */
export function useCanvasContextValue(
  onUpdateNodeData?: (nodeId: string, updates: NodeDataRecord) => void,
  onPersistNodeData?: (nodeId: string, updates: NodeDataRecord) => void,
  onExecuteNode?: (nodeId: string) => void
): CanvasContextValue {
  const { getNode, getNodes, setNodes, setEdges } = useReactFlow<ReactFlowNodeData, ReactFlowCanvasEdgeData>()

  const addNode = useCallback(
    (
      type: string,
      position: CanvasPosition,
      data?: NodeDataRecord,
      options?: { autoExecute?: boolean; parentId?: string }
    ): string => {
      assertNoDirectAssetInputPortWrites(
        type,
        '<new>',
        data ?? {},
        'addNode',
      )
      const node = createNode(type, position, omitDirectAssetInputPortWrites(type, data ?? {}))
      const withParent = options?.parentId ? { ...node, parentId: options.parentId } : node
      setNodes((nds) => [...nds, withParent])
      if (options?.autoExecute && onExecuteNode) {
        onExecuteNode(node.id)
      }
      return node.id
    },
    [setNodes, onExecuteNode]
  )

  const addEdge = useCallback(
    (source: string, sourceHandle: string, target: string, targetHandle: string): void => {
      // Validate type compatibility (BUILD_ORDER Q1)
      const sourceNode = getNode(source)
      const targetNode = getNode(target)

      const sourcePortType = resolvePortType(sourceNode?.type, sourceHandle, 'outputs')
      const targetPortType = resolvePortType(targetNode?.type, targetHandle, 'inputs')

      // If both port types are found, enforce compatibility
      // If either is unknown (dynamic ports, unknown node type), allow the connection
      if (sourcePortType && targetPortType) {
        if (!areTypesCompatible(sourcePortType, targetPortType)) {
          return // Silent no-op on invalid connections
        }
      }

      const edgeId = `e-${source}-${sourceHandle}-${target}-${targetHandle}`
      const newEdge: ReactFlowCanvasEdge = {
        id: edgeId,
        source,
        sourceHandle,
        target,
        targetHandle,
      }
      setEdges((eds) => [...eds, newEdge])
    },
    [getNode, setEdges]
  )

  const removeNode = useCallback(
    (nodeId: string): void => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId))
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
    },
    [setNodes, setEdges]
  )

  const removeEdge = useCallback(
    (edgeId: string): void => {
      setEdges((eds) => eds.filter((e) => e.id !== edgeId))
    },
    [setEdges]
  )

  const duplicateNode = useCallback(
    (nodeId: string): string | undefined => {
      const node = getNode(nodeId)
      if (!node) return undefined

      const clone = structuredClone(node)
      clone.id = crypto.randomUUID()
      clone.position = {
        x: node.position.x + 50,
        y: node.position.y + 50,
      }
      clone.selected = false
      if (clone.data) {
        clone.data.status = 'idle'
        delete clone.data.error
      }
      setNodes((nds) => [...nds, clone])
      return clone.id
    },
    [getNode, setNodes]
  )

  const updateNodeData = useCallback(
    (nodeId: string, updates: NodeDataRecord): void => {
      const node = getNode(nodeId)
      assertNoDirectAssetInputPortWrites(
        node?.type,
        nodeId,
        updates,
        'updateNodeData',
      )

      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, ...updates } } : n
        )
      )
      onUpdateNodeData?.(nodeId, updates)
    },
    [getNode, setNodes, onUpdateNodeData]
  )

  const persistNodeData = useCallback(
    (nodeId: string, updates: NodeDataRecord): void => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, ...updates } } : n
        )
      )
      onPersistNodeData?.(nodeId, updates)
    },
    [setNodes, onPersistNodeData]
  )

  const executeNode = useCallback(
    (nodeId: string): void => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, status: 'running' } } : n
        )
      )
      onExecuteNode?.(nodeId)
    },
    [setNodes, onExecuteNode]
  )

  return useMemo(
    () => ({
      addNode,
      addEdge,
      removeNode,
      removeEdge,
      duplicateNode,
      updateNodeData,
      persistNodeData,
      updateNodeParent: () => {},
      updateNodePosition: () => {},
      executeNode,
      getNode,
      getNodes,
      replacePortEdges: () => {},
    }),
    [
      addNode,
      addEdge,
      removeNode,
      removeEdge,
      duplicateNode,
      updateNodeData,
      persistNodeData,
      executeNode,
      getNode,
      getNodes,
    ]
  )
}
